import { type LoaderFunctionArgs, redirect } from 'react-router-dom';

import { store } from 'src/app/store';
import { LOGIN_REFERRER, THM_USER_ID_KEY } from 'src/common/constants';
import { RoomDetailsStatusCodes, UserAccountTypes, UserGoal, UserLoginTypes, UserRoles } from 'src/common/enums';
import { gb, gbPromise } from 'src/common/growthbook';
import type { AppFeatures } from 'src/common/interfaces/growthbook-generated-types';
import { commonEndpoints } from 'src/common/slices';
import { type CompanyFeatureName, isFeatureEnabled } from 'src/common/utils/company-features';
import { checkIsInInfluencersCampaign } from 'src/features/campaigns/influencers/utils/influencer-campaign-checker-for-router';
import { roomEndpoints } from 'src/features/room/room.slice';

import * as RouteLoaders from './route-loaders';

type CustomLoaderFunction = (loadersArgs: LoaderFunctionArgs) => Promise<ReturnType<typeof redirect> | null>;

const isLoaderValid = (result: boolean | null): boolean => !!result || result === null;

export const getLegacyUrlString = (url: URL) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const origin = isDevelopment ? 'http://localhost:1337' : window.location.origin.toString();
  return origin + url.pathname.replace('/r', '') + url.search;
};

// eslint-disable-next-line @typescript-eslint/require-await -- to conform to the type
export const backToTheLegacyLoader: (loadersArgs: LoaderFunctionArgs) => ReturnType<typeof redirect> = ({
  request,
}) => {
  if (gb.isOn('remove-slash-r-prefix')) {
    // eslint-disable-next-line no-console
    console.error(`Unexpected legacy redirect for ${request.url}`);
    // in this case legacy redirect should never happen, so throw a 404
    return redirect('/not-found');
  }
  const urlObject = new URL(request.url);
  const legacyUrlString = getLegacyUrlString(urlObject);
  return redirect(legacyUrlString);
};

/**
 * !!!
 * For most pages and components we currently expect to load the component and show
 * a skeleton UI while the data loads instead of using this protector.
 * !!!
 */
export const routeProtector = () => {
  const protectorArray: CustomLoaderFunction[] = [];
  // this is the final function that gets returned after each chained function call
  const processFlow = async (loadersArgs: LoaderFunctionArgs) => {
    const promises = protectorArray.map((protector) => protector(loadersArgs));
    const results = await Promise.all(promises);
    const redirectResponse = results.find((result) => result !== null);
    if (redirectResponse) return redirectResponse;
    return null;
  };
  // the below functions are the chainable, each adding a new protector to the protectorArray
  // they are defined directly as properties of the processFlow function object (JavaScript is weird)
  processFlow.requireLogin = (redirectUrl?: string) => {
    protectorArray.push(async ({ request }) => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        await query.unwrap();
      } catch {
        const requestUrl = new URL(request.url);
        const referrer = requestUrl.pathname.replace('/r/', '/') + requestUrl.search;
        window.sessionStorage.setItem(LOGIN_REFERRER, referrer);

        return redirect(redirectUrl ?? `/login${requestUrl.search}`);
      } finally {
        // ! It is important to unsubscribe here, otherwise subscription will always be active.
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireNotLoggedIn = (redirectUrl: string) => {
    protectorArray.push(async ({ request }) => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        await query.unwrap();
        // Preserve the query string when redirecting
        const url = new URL(redirectUrl, window.location.origin); // Ensure absolute URL handling
        const originalParams = new URLSearchParams(window.location.search);
        const paramsFromRequest = new URL(request.url).searchParams;

        // Append new params to the existing ones in redirectUrl
        for (const [key, value] of originalParams) {
          if (!url.searchParams.has(key)) url.searchParams.append(key, value);
        }

        for (const [key, value] of paramsFromRequest) {
          if (!url.searchParams.has(key)) url.searchParams.append(key, value);
        }

        return redirect(url.toString());
      } catch {
        return null;
      } finally {
        query.unsubscribe();
      }
    });
    return processFlow;
  };
  processFlow.requireCorporatePartner = () => {
    protectorArray.push(async ({ params }) => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const hasCompanies = !!data?.user.companies.find(({ _id }) => _id === params?.companyId);
        if (!hasCompanies) throw new Error('user is not part of the company');
      } catch {
        return redirect('/login');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireAdmin = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isAdmin = !!(data?.user.accountType === UserAccountTypes.ADMIN);
        if (!isAdmin) throw new Error('user is not an admin');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireFeatureFlag = (flagName: keyof AppFeatures, redirectUrl?: string) => {
    protectorArray.push(async () => {
      await gbPromise;
      if (!gb.isOn(flagName)) {
        return redirect(redirectUrl ?? '/not-found');
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireRoomPaywallCheck = () => {
    protectorArray.push(async (loadersArgs) => {
      const roomCode = loadersArgs.params?.roomCode;
      if (!roomCode) return null;
      const query = store.dispatch(roomEndpoints.getRoomDetail.initiate(roomCode));
      try {
        const { data } = await query.unwrap();
        if (data?.status === RoomDetailsStatusCodes.SUBSCRIBER_ONLY) {
          const requestUrl = new URL(loadersArgs.request.url);
          let redirectUrl = `/why-subscribe?roomCode=${roomCode}`;
          if (requestUrl.search) {
            redirectUrl += `&${requestUrl.search.slice(1)}`;
          }
          return redirect(redirectUrl);
        }
      } catch {
        return null;
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireBusinessUserOrCorporatePartner = () => {
    protectorArray.push(async () => {
      try {
        const isBusinessUser = await RouteLoaders.isBusinessUserOrCoporatePartner();
        if (!isBusinessUser) throw new Error('user is not part of a company');
      } catch {
        return redirect('/login');
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireAnalyst = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isAnalyst = !!(
          data?.user.accountType === UserAccountTypes.ADMIN || data?.user.roles?.includes(UserRoles.ANALYST)
        );
        if (!isAnalyst) throw new Error('user is not an admin or analyst');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireMod = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isMod = !!(
          data?.user.accountType === UserAccountTypes.ADMIN || data?.user.roles?.includes(UserRoles.MOD)
        );
        if (!isMod) throw new Error('user is not an admin or mod');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireTester = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();

        const isTester = !!(
          data?.user.accountType === UserAccountTypes.ADMIN ||
          data?.user.roles?.includes(UserRoles.TESTER) ||
          data?.user.roles?.includes(UserRoles.BETA_TESTER)
        );
        if (!isTester) throw new Error('user is not an admin or tester or beta-tester');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireSDR = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isSDR = !!(
          data?.user.accountType === UserAccountTypes.ADMIN ||
          data?.user.roles?.includes(UserRoles.SDR) ||
          data?.user.roles?.includes(UserRoles.BDM)
        );
        if (!isSDR) throw new Error('user is not an admin or SDR');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireBDM = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isBDM = !!(
          data?.user.accountType === UserAccountTypes.ADMIN || data?.user.roles?.includes(UserRoles.BDM)
        );
        if (!isBDM) throw new Error('user is not an admin or BDM');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireSDROrAnalyst = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isSDRorAnalyst = !!(
          data?.user.accountType === UserAccountTypes.ADMIN ||
          data?.user.roles?.includes(UserRoles.SDR) ||
          data?.user.roles?.includes(UserRoles.BDM) ||
          data?.user.roles?.includes(UserRoles.ANALYST)
        );
        if (!isSDRorAnalyst) throw new Error('user is not an admin or SDR or BDM or Analyst');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireLocalUser = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isLocalUser = data?.user.local.loginType === UserLoginTypes.LOCAL;
        if (!isLocalUser) throw new Error('user is not a local user');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });

    return processFlow;
  };
  processFlow.requireTagAdmin = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isTaggingAdmin = !!(
          data?.user.accountType === UserAccountTypes.ADMIN || data?.user.roles?.includes(UserRoles.TAG_ADMIN)
        );
        if (!isTaggingAdmin) throw new Error('user is not an admin or tag admin');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireSSOFeatureEnabled = () => {
    protectorArray.push(async ({ params }) => {
      const { companyId } = params;

      if (!companyId) return redirect('/dashboard');

      const query = store.dispatch(commonEndpoints.getCompany.initiate(companyId));
      try {
        const { features } = await query.unwrap();
        if (!features.sso) throw new Error('SSO is not enabled for company');
      } catch {
        return redirect(`/client/${companyId}`);
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireCompletedOnboardingAndWelcome = () => {
    protectorArray.push(async () => {
      await gbPromise;

      const isInfluencerCampaign = await checkIsInInfluencersCampaign();

      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();

        if (data) {
          if ([UserGoal.EVALUTE_THM, UserGoal.TRAIN_TEAM].includes(data?.user?.demographics?.goal as UserGoal)) {
            const hasCompletedOnboarding = !!data?.user?.demographics?.howHeard;
            if (!hasCompletedOnboarding) {
              return redirect('/onboarding');
            }
          } else {
            const hasCompletedOnboarding = !!data?.user?.demographics?.howHeard;
            const hasCompletedWelcome = !!data?.user?.local?.path;
            const isB2BUser =
              (data?.user?.companies && data.user.companies.length > 0) ||
              data?.user?.subscriptions?.some((subscription) => subscription.companyId != null);
            if (isB2BUser) {
              return null;
            }

            if (!hasCompletedOnboarding && !hasCompletedWelcome) {
              return redirect('/onboarding');
            }
            if (!hasCompletedWelcome && !isInfluencerCampaign) {
              return redirect('/welcome');
            }
          }
        }
      } catch {
        // The user should be logged out if we get here
        return null;
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireNotSubscribed = () => {
    protectorArray.push(async (loadersArgs) => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());

      try {
        const { data } = await query.unwrap();
        const isPremiumUser = data?.user?.isPremium && !data?.user?.isPaused;

        if (isPremiumUser) {
          return await backToTheLegacyLoader(loadersArgs);
        }
      } catch {
        // The user should be logged out if we get here
        return null;
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };
  processFlow.requireOneOfRoles = (roles: UserRoles[]) => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const hasRoleMatching = data?.user.roles?.find((role) => roles.includes(role));
        const roleMatched = !!(data?.user.accountType === UserAccountTypes.ADMIN || hasRoleMatching);
        if (!roleMatched) throw new Error('user is not an admin or has no matching role');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireNotOneOfRoles = (roles: UserRoles[]) => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const hasRoleMatching = data?.user.roles?.find((role) => roles.includes(role));
        if (hasRoleMatching) throw new Error('user has a matching role');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireSocSimFeatureEnabled = () => {
    protectorArray.push(async ({ params }) => {
      const { companyId } = params;

      if (!companyId) return redirect('/dashboard');

      const query = store.dispatch(commonEndpoints.getCompany.initiate(companyId));
      try {
        const { features } = await query.unwrap();
        if (!features.socSim) throw new Error('SOC SIM is not enabled for company');
      } catch {
        return redirect(`/client/${companyId}`);
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireThreatHuntingFeatureEnabled = () => {
    protectorArray.push(async ({ params }) => {
      const { companyId } = params;

      if (!companyId) return redirect('/dashboard');

      const query = store.dispatch(commonEndpoints.getCompany.initiate(companyId));
      try {
        const { features } = await query.unwrap();
        if (!features.threatHunting) throw new Error('Threat Hunting is not enabled for company');
      } catch {
        return redirect(`/client/${companyId}`);
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireFeatureEnabled = (featureName: CompanyFeatureName, redirectUrl?: string) => {
    protectorArray.push(async ({ params }) => {
      const { companyId } = params;
      if (!companyId) return redirect('/dashboard');

      const query = store.dispatch(commonEndpoints.getCompany.initiate(companyId));
      try {
        const { features } = await query.unwrap();
        if (!isFeatureEnabled(features, featureName)) {
          throw new Error(`${featureName} is not enabled for company`);
        }
      } catch {
        const redirectPath = redirectUrl ?? `/client/${companyId}`;
        return redirect(redirectPath);
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireCategorizationRuleDev = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isCategorizationRuleDev = !!(
          data?.user.accountType === UserAccountTypes.ADMIN ||
          data?.user.roles?.includes(UserRoles.CATEGORIZATION_RULE_DEV)
        );
        if (!isCategorizationRuleDev) throw new Error('user is not an admin or categorization rule developer');
      } catch {
        return redirect('/404');
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.waitForUserData = () => {
    protectorArray.push(async () => {
      const query = store.dispatch(commonEndpoints.getUser.initiate());
      try {
        const { data } = await query.unwrap();
        const isSharerIdEnabled = gb.isOn('tracking-sharer-id');
        if (isSharerIdEnabled && data?.user?._id) localStorage.setItem(THM_USER_ID_KEY, data.user._id);
      } catch {
        /* empty */
      } finally {
        query.unsubscribe();
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireWorkspaceUser = () => {
    protectorArray.push(async () => {
      try {
        const isParOfAWorkspace = await RouteLoaders.userIsPartOfAWorkspace();
        if (!isParOfAWorkspace) throw new Error('user is not part of a workspace');
      } catch {
        return redirect('/workspace?ref=ctf-builder');
      }
      return null;
    });
    return processFlow;
  };

  processFlow.requireOneOf = ({
    redirectTo = '/dashboard',
    loaders,
  }: {
    redirectTo: string;
    loaders: (() => Promise<null | boolean>)[];
  }) => {
    protectorArray.push(async () => {
      try {
        const results = await Promise.all(loaders.map((item) => item()));
        if (results.some((element) => isLoaderValid(element))) {
          return null;
        }
        throw new Error('Some of the loaders failed');
      } catch {
        return redirect(redirectTo);
      }
    });
    return processFlow;
  };

  return processFlow;
};
